/**
 * ColumnsSection.jsx - Customize > Columns (round 4, 10/9/26). One list:
 * drag a row (or ↑ ↓) to reorder, a switch to show or hide, a pin to freeze
 * the column at the left, and "⋯" for its width, header and cell text size
 * and alignment. A search box finds a column; "14 of 22 shown"; Select all,
 * Unselect all (the first column in the list stays - a table is never
 * empty) and Reset columns.
 */
import { useState } from 'react';
import { Switch, Segmented, SectionHead } from './controls';
import { FONT_SIZES, MAX_PINNED, MIN_WIDTH, MAX_WIDTH } from '../viewConfig';
import { usePointerSort } from '../usePointerSort';

const SMALL_BTN =
  'w-6 h-6 inline-flex items-center justify-center rounded border border-border-strong text-[11px] bg-surface hover:bg-surface-2 disabled:opacity-30';
const SELECT = 'border border-border-strong rounded px-1.5 py-1 text-[11.5px] bg-surface';

/** A width box: typed freely, set on Enter or leaving it (clamped 40-600), blank = auto. */
function WidthInput({ label, value, onSet, onClear }) {
  const [draft, setDraft] = useState(null);
  const commit = () => {
    if (draft == null) return;
    if (String(draft).trim() === '') onClear();
    else if (Number(draft) > 0) onSet(Number(draft));
    setDraft(null);
  };
  return (
    <span className="flex gap-1 items-center">
      <input
        type="number"
        min={MIN_WIDTH}
        max={MAX_WIDTH}
        step={5}
        aria-label={label}
        className={`${SELECT} w-20`}
        placeholder="auto"
        value={draft ?? (value || '')}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            commit();
          }
        }}
      />
      {value && (
        <button type="button" className="text-gat-greenDark font-semibold" onClick={onClear}>
          auto
        </button>
      )}
    </span>
  );
}

export default function ColumnsSection({ view, defs }) {
  const [q, setQ] = useState('');
  const [open, setOpen] = useState(null); // the column whose details are shown
  const byId = Object.fromEntries(defs.map((d) => [d.col, d]));
  const labelOf = (c) => (byId[c] ? byId[c].label : c);
  const hidden = new Set(view.hidden);
  const shownCount = view.order.filter((c) => !hidden.has(c)).length;
  const pinned = view.pinned || [];
  const list = view.order.filter((c) => !q || labelOf(c).toLowerCase().includes(q.trim().toLowerCase()));
  const sort = usePointerSort({ axis: 'y', onDrop: (col, to) => view.reorder(col, to, 'all') });

  return (
    <div>
      <SectionHead
        title="Columns"
        hint="Drag a column to move it, switch it on or off, pin it to keep it at the left while you scroll. ⋯ for its width, text size and alignment."
        onReset={() => view.resetSection('columns')}
        resetLabel="Reset columns"
      />
      <div className="flex items-center gap-2 mb-2 flex-wrap">
        <input
          aria-label="Find a column"
          placeholder="Find a column…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          className="flex-1 min-w-[140px] border border-border-strong rounded px-2 py-1.5 text-[12px]"
        />
        <span className="text-[11.5px] text-text-muted whitespace-nowrap" data-testid="columns-count">
          {shownCount} of {view.order.length} shown
        </span>
      </div>
      <div className="flex gap-3 mb-2 text-[11.5px] font-semibold">
        <button type="button" className="text-gat-greenDark hover:underline" onClick={view.selectAll}>
          Select all
        </button>
        <button
          type="button"
          className="text-gat-greenDark hover:underline"
          onClick={view.unselectAll}
          title="Keeps the first column in the list"
        >
          Unselect all
        </button>
        <button
          type="button"
          className="text-gat-greenDark hover:underline disabled:opacity-40 disabled:no-underline"
          onClick={view.resetWidths}
          disabled={!Object.keys(view.widths || {}).length}
        >
          Reset widths
        </button>
        <button
          type="button"
          className="text-gat-greenDark hover:underline disabled:opacity-40 disabled:no-underline"
          onClick={view.resetFonts}
          disabled={!Object.keys(view.fonts || {}).length}
        >
          Reset sizes
        </button>
      </div>
      <ul
        ref={sort.ref}
        onPointerDown={sort.onPointerDown}
        aria-label="Columns"
        className="m-0 p-0 list-none border border-border rounded-md divide-y divide-border"
      >
        {list.map((c) => {
          const on = !hidden.has(c);
          const i = view.order.indexOf(c);
          const isPinned = pinned.includes(c);
          const w = view.widths && view.widths[c];
          const f = (view.fonts && view.fonts[c]) || {};
          const expanded = open === c;
          return (
            <li key={c} data-sort-key={c} data-sort-label={labelOf(c)} data-col={c} className="bg-surface text-[12px] select-none">
              <div className="flex items-center gap-2 px-2.5 py-1.5">
                <span className="cursor-grab text-text-muted" aria-hidden="true" title="Drag to move">
                  ⋮⋮
                </span>
                <Switch small on={on} disabled={on && shownCount <= 1} onChange={() => view.toggleColumn(c)} label={`Show ${labelOf(c)}`} />
                <span className={`flex-1 truncate ${on ? 'text-text-dark font-medium' : 'text-text-muted'}`}>{labelOf(c)}</span>
                {w && (
                  <span className="text-[10.5px] text-text-muted" title="A width you set">
                    {w}px
                  </span>
                )}
                <button
                  type="button"
                  onClick={() => view.togglePin(c)}
                  aria-pressed={isPinned}
                  aria-label={`Pin ${labelOf(c)}`}
                  disabled={!isPinned && pinned.length >= MAX_PINNED}
                  title={isPinned ? 'Unpin' : 'Pin to the left: stays in view while the table scrolls sideways'}
                  className={`${SMALL_BTN} ${isPinned ? '!bg-gat-green !text-white !border-gat-greenDark' : ''}`}
                >
                  📌
                </button>
                <button
                  type="button"
                  onClick={() => setOpen(expanded ? null : c)}
                  aria-expanded={expanded}
                  aria-label={`More for ${labelOf(c)}`}
                  className={SMALL_BTN}
                >
                  ⋯
                </button>
                <button
                  type="button"
                  className={SMALL_BTN}
                  disabled={i === 0}
                  onClick={() => view.moveColumn(c, -1)}
                  aria-label={`Move ${labelOf(c)} up`}
                >
                  ↑
                </button>
                <button
                  type="button"
                  className={SMALL_BTN}
                  disabled={i === view.order.length - 1}
                  onClick={() => view.moveColumn(c, 1)}
                  aria-label={`Move ${labelOf(c)} down`}
                >
                  ↓
                </button>
              </div>
              {expanded && (
                <div className="px-3 pb-3 pt-1 grid grid-cols-2 gap-x-4 gap-y-2 bg-surface-2 text-[11.5px]" data-no-drag="">
                  <div className="flex flex-col gap-1">
                    <span className="text-text-muted">Width (px)</span>
                    <WidthInput
                      label={`Width of ${labelOf(c)}`}
                      value={w}
                      onSet={(px) => view.setWidth(c, px)}
                      onClear={() => view.clearWidth(c)}
                    />
                  </div>
                  <div className="flex flex-col gap-1">
                    <span className="text-text-muted">Alignment</span>
                    <Segmented
                      label={`Alignment of ${labelOf(c)}`}
                      value={(view.align && view.align[c]) || ''}
                      onChange={(a) => view.setAlign(c, a || null)}
                      options={[
                        ['', 'Auto'],
                        ['left', 'Left'],
                        ['center', 'Center'],
                        ['right', 'Right'],
                      ]}
                    />
                  </div>
                  <label className="flex flex-col gap-1">
                    <span className="text-text-muted">Header text size</span>
                    <select
                      aria-label={`Header size for ${labelOf(c)}`}
                      className={SELECT}
                      value={f.head || ''}
                      onChange={(e) => view.setFont(c, 'head', e.target.value ? Number(e.target.value) : null)}
                    >
                      <option value="">Table&rsquo;s own</option>
                      {FONT_SIZES.map((n) => (
                        <option key={n} value={n}>
                          {n}px
                        </option>
                      ))}
                    </select>
                  </label>
                  <label className="flex flex-col gap-1">
                    <span className="text-text-muted">Cell text size</span>
                    <select
                      aria-label={`Cell size for ${labelOf(c)}`}
                      className={SELECT}
                      value={f.cell || ''}
                      onChange={(e) => view.setFont(c, 'cell', e.target.value ? Number(e.target.value) : null)}
                    >
                      <option value="">Table&rsquo;s own</option>
                      {FONT_SIZES.map((n) => (
                        <option key={n} value={n}>
                          {n}px
                        </option>
                      ))}
                    </select>
                  </label>
                </div>
              )}
            </li>
          );
        })}
        {!list.length && <li className="px-2.5 py-2 text-[12px] text-text-muted">No column matches “{q}”.</li>}
      </ul>
    </div>
  );
}
